import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import CalendarHeader from "@/components/calendar/CalendarHeader";
import MonthView from "@/components/calendar/MonthView";
import WeekView from "@/components/calendar/WeekView";
import DayView from "@/components/calendar/DayView";
import EventDialog from "@/components/events/EventDialog";
import { EventType } from "@/types/event";
import { allEvents } from "@/data/mockData";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

type ViewType = "month" | "week" | "day";

// Define a type for the events from Supabase that matches the actual database schema
interface DbEvent {
  id: string;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  location?: string;
  created_by: string;
  group_id?: string;
}

const Calendar = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState<ViewType>("month");
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [userEvents, setUserEvents] = useState<EventType[]>([]);
  const [sharedEvents, setSharedEvents] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Use real user authentication status, ensure demo mode is only for actual demo users
  const isDemoMode = !isAuthenticated || user?.id === "demo-user-id";

  // Fetch events when authenticated user logs in
  useEffect(() => {
    const fetchEvents = async () => {
      if (!isDemoMode && user) {
        try {
          setLoading(true);
          console.log("Fetching events for user:", user.id);
          
          // 1. Fetch user's own events
          const { data: ownEventsData, error: ownEventsError } = await supabase
            .from('ft_events')
            .select('*, ft_groups(color, name)')
            .eq('created_by', user.id);
            
          if (ownEventsError) {
            console.error("Error fetching own events:", ownEventsError);
            toast({
              title: "Error",
              description: "Failed to load your calendar events. Please try again.",
              variant: "destructive",
            });
          }
          
          // 2. Fetch events shared with this user based on friend/family relationship
          const { data: userProfile, error: profileError } = await supabase
            .from('ft_profiles')
            .select('email')
            .eq('id', user.id)
            .single();
            
          if (profileError) {
            console.error("Error fetching user profile:", profileError);
          } else if (userProfile) {
            // Get all friend connections where this user is the recipient
            const { data: friendships, error: friendshipsError } = await supabase
              .from('ft_friend_invites')
              .select('sender_id, relationship')
              .eq('recipient_email', userProfile.email)
              .eq('status', 'accepted');
              
            if (friendshipsError) {
              console.error("Error fetching friendships:", friendshipsError);
            } else if (friendships && friendships.length > 0) {
              console.log("Found friendships:", friendships);
              
              // For each friendship, fetch the appropriate shared events
              const sharedEventsPromises = friendships.map(async (friendship) => {
                // Get the groups this user should have access to based on relationship
                const { data: friendGroups, error: groupsError } = await supabase
                  .from('ft_groups')
                  .select('id, name, color')
                  .eq('created_by', friendship.sender_id)
                  .eq('type', friendship.relationship.toLowerCase());
                  
                if (groupsError) {
                  console.error(`Error fetching groups for ${friendship.sender_id}:`, groupsError);
                  return [];
                }
                
                if (!friendGroups || friendGroups.length === 0) {
                  return [];
                }
                
                // Get events from these groups
                const groupIds = friendGroups.map(g => g.id);
                const { data: friendEvents, error: eventsError } = await supabase
                  .from('ft_events')
                  .select('*, ft_groups(color, name)')
                  .eq('created_by', friendship.sender_id)
                  .in('group_id', groupIds);
                  
                if (eventsError) {
                  console.error(`Error fetching events for ${friendship.sender_id}:`, eventsError);
                  return [];
                }
                
                return friendEvents || [];
              });
              
              // Wait for all shared events to be fetched
              const allSharedEventsArrays = await Promise.all(sharedEventsPromises);
              const allSharedEvents = allSharedEventsArrays.flat();
              
              console.log("Fetched shared events:", allSharedEvents);
              
              // Map shared events to our EventType format
              if (allSharedEvents.length > 0) {
                const mappedSharedEvents: EventType[] = allSharedEvents.map((event: any) => ({
                  id: event.id,
                  title: event.title,
                  description: event.description || "",
                  startDate: event.start_date,
                  endDate: event.end_date,
                  location: event.location || "",
                  color: event.ft_groups?.color || "bg-blue-100 text-blue-800",
                  attendees: [],
                  created_by: event.created_by,
                  group: event.ft_groups?.name || "",
                  isShared: true // Mark as shared event
                }));
                
                // Process multi-day events for shared events
                const expandedSharedEvents = expandMultiDayEvents(mappedSharedEvents);
                setSharedEvents(expandedSharedEvents);
              }
            }
          }
          
          // Process user's own events
          if (ownEventsData) {
            console.log("Fetched own events:", ownEventsData);
            
            // Map Supabase data to our EventType format with correct colors
            const mappedEvents: EventType[] = ownEventsData.map((event: any) => ({
              id: event.id,
              title: event.title,
              description: event.description || "",
              startDate: event.start_date,
              endDate: event.end_date,
              location: event.location || "",
              color: event.ft_groups?.color || "bg-blue-100 text-blue-800", // Use group color
              attendees: [],
              created_by: event.created_by,
              group: event.ft_groups?.name || "", // Map group_id to group in our EventType
              isOwn: true // Mark as user's own event
            }));
            
            // Process multi-day events - create spanning events for calendar display
            const expandedEvents = expandMultiDayEvents(mappedEvents);
            setUserEvents(expandedEvents);
          }
        } catch (err) {
          console.error("Error in fetchEvents:", err);
          toast({
            title: "Error",
            description: "Failed to load calendar events. Please try again.",
            variant: "destructive",
          });
          setUserEvents([]);
          setSharedEvents([]);
        } finally {
          setLoading(false);
        }
      } else {
        // For demo users, we use the mock data
        setUserEvents([]);
        setSharedEvents([]);
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [isDemoMode, user, toast]);
  
  // Helper function to expand multi-day events into separate display events for each day
  const expandMultiDayEvents = (events: EventType[]): EventType[] => {
    const expandedEvents: EventType[] = [];
    
    events.forEach(event => {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      
      // If start and end are on the same day, just add the original event
      if (startDate.toDateString() === endDate.toDateString()) {
        expandedEvents.push(event);
        return;
      }
      
      // Otherwise, create separate display events for each day in the range
      let currentDay = new Date(startDate);
      while (currentDay <= endDate) {
        const isFirstDay = currentDay.toDateString() === startDate.toDateString();
        const isLastDay = currentDay.toDateString() === endDate.toDateString();
        
        const displayEvent: EventType = {
          ...event,
          id: `${event.id}-${currentDay.toISOString().split('T')[0]}`, // Unique ID for display purposes only
          originalEventId: event.id, // Keep reference to original event
          isMultiDay: true,
          isFirstDay,
          isLastDay,
          // For display only, each day's event shows that day's span
          displayStartDate: isFirstDay ? event.startDate : new Date(currentDay).toISOString(),
          displayEndDate: isLastDay ? event.endDate : new Date(new Date(currentDay).setHours(23, 59, 59, 999)).toISOString()
        };
        
        expandedEvents.push(displayEvent);
        
        // Move to next day
        currentDay.setDate(currentDay.getDate() + 1);
      }
    });
    
    return expandedEvents;
  };
  
  const handleSelectDate = (date: Date) => {
    setDate(date);
    if (view === "month") {
      setView("day");
    }
  };
  
  const handleSelectEvent = (event: EventType) => {
    // If this is a display event for a multi-day event, find the original event
    const allEvents = [...userEvents, ...sharedEvents];
    
    if (event.originalEventId) {
      const originalEvent = allEvents.find(e => e.id === event.originalEventId && !e.originalEventId);
      setSelectedEvent(originalEvent || event);
    } else {
      setSelectedEvent(event);
    }
    setIsEventDialogOpen(true);
  };
  
  const handleCreateEvent = (date?: Date) => {
    if (isDemoMode) {
      toast({
        title: "Demo Mode",
        description: "This is a demonstration calendar. Sign up for a full account to create events.",
        variant: "default",
      });
      return;
    }
    
    // When we have a selected date, pass it to the create event page
    if (date) {
      navigate("/create-event", { 
        state: { 
          initialDate: date.toISOString() 
        } 
      });
    } else {
      navigate("/create-event");
    }
  };
  
  const renderCalendarView = () => {
    // For authenticated users, use their calendar data AND shared events
    // For demo users, use ONLY the mock data 
    const eventsToShow = isDemoMode ? allEvents : [...userEvents, ...sharedEvents];
    
    switch (view) {
      case "month":
        return (
          <MonthView
            date={date}
            events={eventsToShow}
            onSelectDate={handleSelectDate}
            onCreateEvent={handleCreateEvent}
            isDemoMode={isDemoMode}
          />
        );
      case "week":
        return (
          <WeekView
            date={date}
            events={eventsToShow}
            onSelectDate={handleSelectDate}
            onSelectEvent={handleSelectEvent}
            onCreateEvent={handleCreateEvent}
            isDemoMode={isDemoMode}
          />
        );
      case "day":
        return (
          <DayView
            date={date}
            events={eventsToShow}
            onSelectTime={handleCreateEvent}
            onSelectEvent={handleSelectEvent}
            isDemoMode={isDemoMode}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <>
      <h1 className="text-2xl font-bold mb-6 text-black">
        {isDemoMode ? "Calendar Demo" : "Calendar"}
        {isDemoMode && (
          <span className="ml-2 text-sm bg-blue-100 text-blue-800 py-1 px-2 rounded-full">
            Demo Mode
          </span>
        )}
        {!isDemoMode && sharedEvents.length > 0 && (
          <span className="ml-2 text-sm bg-green-100 text-green-800 py-1 px-2 rounded-full">
            {sharedEvents.length} Shared Events
          </span>
        )}
      </h1>
      
      <Card>
        <CardContent className="p-4">
          <CalendarHeader
            date={date}
            setDate={setDate}
            view={view}
            setView={setView}
          />
          <div className="h-[calc(100vh-280px)] overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              renderCalendarView()
            )}
          </div>
        </CardContent>
      </Card>
      
      <EventDialog
        event={selectedEvent}
        open={isEventDialogOpen}
        onOpenChange={setIsEventDialogOpen}
      />
    </>
  );
};

export default Calendar;
